import type { ReactNode } from 'react'

import { sentence } from '@/lib/format'

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

/* ------------------------------------------------------------------ layout */

export function Card({
  children,
  className,
  title,
  subtitle,
  action,
}: {
  children: ReactNode
  className?: string
  title?: ReactNode
  subtitle?: ReactNode
  action?: ReactNode
}) {
  return (
    <section className={cx('card', className)}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-4 border-b border-line px-4 py-3">
          <div className="min-w-0">
            {title && <h2 className="text-sm font-semibold text-ink">{title}</h2>}
            {subtitle && <p className="mt-0.5 text-xs text-ink-muted">{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  )
}

export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string
  subtitle?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-1 max-w-2xl text-sm text-ink-muted">{subtitle}</p>}
      </div>
      {action}
    </div>
  )
}

/* ------------------------------------------------------------------ states */

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-sm text-ink-muted">
      <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" aria-hidden="true">
        <circle
          cx="12" cy="12" r="9" fill="none" stroke="currentColor"
          strokeWidth="2.5" strokeLinecap="round" strokeDasharray="40 20"
        />
      </svg>
      <span>{label}…</span>
    </div>
  )
}

export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-6">
      <p className="flex items-center gap-2 text-sm text-ink">
        <span aria-hidden="true" style={{ color: 'var(--critical)' }}>
          ⚠
        </span>
        {message}
      </p>
      {onRetry && (
        <button type="button" className="btn-secondary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  )
}

export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="px-4 py-14 text-center">
      <p className="text-sm font-medium text-ink">{title}</p>
      {hint && <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-muted">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------ badges */

type Tone = 'neutral' | 'info' | 'good' | 'warning' | 'critical'

const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  neutral: { bg: 'var(--surface-2)', fg: 'var(--ink-2)' },
  info: { bg: 'var(--wash)', fg: 'var(--series-1)' },
  good: { bg: 'color-mix(in srgb, var(--good) 14%, transparent)', fg: 'var(--good)' },
  warning: { bg: 'color-mix(in srgb, var(--serious) 18%, transparent)', fg: 'var(--serious)' },
  critical: { bg: 'color-mix(in srgb, var(--critical) 14%, transparent)', fg: 'var(--critical)' },
}

export function Badge({
  children,
  tone = 'neutral',
  icon,
}: {
  children: ReactNode
  tone?: Tone
  icon?: string
}) {
  const style = TONE_STYLE[tone]
  return (
    <span
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium"
      style={{ background: style.bg, color: style.fg }}
    >
      {/* Status colour never carries meaning alone - it ships with an icon and a label. */}
      {icon && <span aria-hidden="true">{icon}</span>}
      {children}
    </span>
  )
}

const STATUS_TONES: Record<string, { tone: Tone; icon?: string }> = {
  new: { tone: 'info' },
  queued: { tone: 'neutral' },
  contacted: { tone: 'neutral' },
  engaged: { tone: 'info' },
  profiled: { tone: 'good', icon: '✓' },
  dormant: { tone: 'neutral' },
  opted_out: { tone: 'critical', icon: '⊘' },
  blocked: { tone: 'critical', icon: '⊘' },
  active: { tone: 'good', icon: '●' },
  awaiting_reply: { tone: 'warning', icon: '◔' },
  paused: { tone: 'neutral', icon: '❚❚' },
  completed: { tone: 'good', icon: '✓' },
  reviewing: { tone: 'warning', icon: '◔' },
  approved: { tone: 'good', icon: '✓' },
  rejected: { tone: 'critical', icon: '✕' },
  introduction_requested: { tone: 'warning', icon: '◔' },
  introduction_sent: { tone: 'info', icon: '↗' },
  connected: { tone: 'good', icon: '✓' },
  no_response: { tone: 'neutral' },
  expired: { tone: 'neutral' },
  pending: { tone: 'neutral' },
  awaiting_consent: { tone: 'warning', icon: '◔' },
  ready: { tone: 'good', icon: '✓' },
  sent: { tone: 'info', icon: '↗' },
  granted: { tone: 'good', icon: '✓' },
  declined: { tone: 'critical', icon: '✕' },
  asked: { tone: 'warning', icon: '◔' },
  abandoned: { tone: 'neutral' },
  failed: { tone: 'critical', icon: '✕' },
}

export function StatusBadge({ status }: { status: string }) {
  const config = STATUS_TONES[status] ?? { tone: 'neutral' as Tone }
  return (
    <Badge tone={config.tone} icon={config.icon}>
      {sentence(status)}
    </Badge>
  )
}

export function AssertionBadge({ assertion }: { assertion: string }) {
  // The explicit/inferred distinction drives scoring and disclosure, so it is
  // always visible rather than implied.
  return assertion === 'explicit' ? (
    <Badge tone="good" icon="❝">
      Stated
    </Badge>
  ) : (
    <Badge tone="warning" icon="~">
      Inferred
    </Badge>
  )
}

/* ------------------------------------------------------------------ tables */

export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[52rem] border-collapse text-sm">{children}</table>
    </div>
  )
}

export function Th({
  children,
  className,
  numeric,
}: {
  children: ReactNode
  className?: string
  numeric?: boolean
}) {
  return (
    <th
      scope="col"
      className={cx(
        'border-b border-line px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-ink-faint',
        numeric ? 'text-right' : 'text-left',
        className,
      )}
    >
      {children}
    </th>
  )
}

export function Td({
  children,
  className,
  numeric,
}: {
  children: ReactNode
  className?: string
  numeric?: boolean
}) {
  return (
    <td
      className={cx(
        'border-b border-line px-4 py-3 align-middle text-ink',
        numeric && 'text-right tabular',
        className,
      )}
    >
      {children}
    </td>
  )
}

/* ------------------------------------------------------------------ inputs */

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: string
  error?: string
  children: ReactNode
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-medium text-ink-muted">{label}</span>
      {children}
      {hint && !error && <span className="mt-1 block text-xs text-ink-faint">{hint}</span>}
      {error && (
        <span className="mt-1 block text-xs" style={{ color: 'var(--critical)' }}>
          {error}
        </span>
      )}
    </label>
  )
}

export function Select({
  value,
  onChange,
  options,
  label,
}: {
  value: string
  onChange: (value: string) => void
  options: Array<{ value: string; label: string }>
  label: string
}) {
  return (
    <select
      aria-label={label}
      className="field w-auto min-w-[9rem] py-1.5 text-sm"
      value={value}
      onChange={(event) => onChange(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  )
}

/* ---------------------------------------------------------------- overlays */

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
}: {
  open: boolean
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  if (!open) return null
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="card max-h-[85vh] w-full max-w-xl overflow-y-auto shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <button type="button" className="btn-ghost px-2 py-1" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="px-4 py-4">{children}</div>
        {footer && (
          <footer className="flex justify-end gap-2 border-t border-line px-4 py-3">{footer}</footer>
        )}
      </div>
    </div>
  )
}

export function Avatar({ name, size = 32 }: { name: string; size?: number }) {
  const letters = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  return (
    <span
      aria-hidden="true"
      className="inline-flex shrink-0 items-center justify-center rounded-full font-medium"
      style={{
        width: size,
        height: size,
        background: 'var(--wash)',
        color: 'var(--series-1)',
        fontSize: size * 0.36,
      }}
    >
      {letters || '?'}
    </span>
  )
}
